import type { ActivatedRouteSnapshot, Routes } from '@angular/router';
import {
    CampaignFeature,
    createActionTriggerDealer,
    createMetaResolverWithDealer,
    DealerFeature,
    defaultTitleResolver,
    extraDealerTitleResolver,
    ngrxLoaderGuard,
    PageContentType,
} from '@hyundai/ng-common-lib';
import { provideEffects } from '@ngrx/effects';
import { provideState } from '@ngrx/store';
import { filter, firstValueFrom, map } from 'rxjs';

export default [
    {
        path: '',
        loadComponent: () => import('./campaign-overview-page/campaign-overview-page.component'),
        canActivate: [
            ngrxLoaderGuard({
                waitUntilLoadedSelector: CampaignFeature.state.selectIsLoadingCampaignOverview,
                dataToCheckSelector: CampaignFeature.state.selectCampaignsOverview,
            }),
        ],
        providers: [
            provideState(CampaignFeature.state),
            provideEffects([CampaignFeature.effects]),
            createActionTriggerDealer(CampaignFeature.actions.loadCampaignOverview()),
        ],
        title: defaultTitleResolver('Unsere Aktionen & Angebote'),
        resolve: {
            meta: createMetaResolverWithDealer(async (dealer) => {
                return {
                    description: `Entdecken Sie unsere Hyundai Modlle bei ${dealer.name} in ${dealer.city}.`,
                };
            }),
        },
        data: {
            contentType: PageContentType.CAMPAIGNOVERVIEW,
        },
    },
    {
        path: ':campaignSlug',
        loadComponent: () => import('./campaign-detail-page/campaign-detail-page.component'),
        canActivate: [
            ngrxLoaderGuard({
                actionToDispatch: (route: ActivatedRouteSnapshot) =>
                    CampaignFeature.actions.loadCampaignDetail({ slug: route.params['campaignSlug'] }),
                waitUntilLoadedSelector: CampaignFeature.state.selectIsLoadingcamapignDetail,
                dataToCheckSelector: CampaignFeature.state.selectCampaignDetail,
                redirectOnFailUrl: (route, store) => {
                    return store.select(DealerFeature.state.selectDealerSlug).pipe(
                        map((slug) => {
                            return `${slug}/404`;
                        }),
                    );
                },
            }),
        ],
        providers: [provideState(CampaignFeature.state), provideEffects([CampaignFeature.effects])],
        title: extraDealerTitleResolver((store) =>
            firstValueFrom(
                store.select(CampaignFeature.state.selectCampaignDetail).pipe(
                    filter((c) => !!c),
                    map((c) => c.title),
                ),
            ),
        ),
        resolve: {
            meta: createMetaResolverWithDealer(async (dealer, store) => {
                const campaignDetail = await firstValueFrom(
                    store.select(CampaignFeature.state.selectCampaignDetail).pipe(filter((c) => !!c)),
                );

                const end = `bei ${dealer.name} in ${dealer.city}.`;

                const startVariants: Record<string, string> = {
                    AfterSale: `Entdecken Sie den Hyundai ${campaignDetail.title} zu attraktiven Konditionen`,
                    SalesCampaignContent: `Entdecken Sie unser Service-Angebot ${campaignDetail.title} und profitieren Sie von exklusiven Vorteilen`,
                };

                return {
                    description: `${startVariants[campaignDetail.__contenttype]} ${end}`,
                };
            }),
        },
        data: {
            contentType: PageContentType.CAMPAIGNDETAIL,
        },
    },
] satisfies Routes;
